import type { FC } from "react";
import { Link } from "react-router";
import { AlertTriangle, ArrowNext, CheckCircle, Gift01, Link03, RefreshCcw01, Stars02 } from "@untitledui/icons";
import type { LevelCardView } from "@mfa-aff/shared";
import { HelpTip, cx, formatJalali, formatNumber, formatPercentBp } from "@mfa-aff/ui";
import { Badge } from "@mfa-aff/ui/components/base/badges/badges";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { ProgressBar } from "@mfa-aff/ui/components/base/progress-indicators/progress-indicators";
import { ContractSwitchAction } from "../levels/ContractSwitch";
import { levelImage, rateCard } from "../levels/levelImages";

const pct = (bp: number | null) => (bp === null ? "—" : formatPercentBp(bp));

const RateTile = ({ icon: Icon, label, value, note }: { icon: FC<{ className?: string }>; label: string; value: string; note: string }) => (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-xl bg-secondary p-3.5 ring-1 ring-secondary ring-inset md:p-4">
        <p className="flex items-center gap-1.5 text-xs font-medium text-tertiary">
            <Icon className="size-4 shrink-0 text-fg-quaternary" aria-hidden="true" />
            <span className="truncate">{label}</span>
        </p>
        <p className="text-display-xs font-semibold text-primary digits-tabular">{value}</p>
        <p className="text-xs leading-5 text-tertiary">{note}</p>
    </div>
);

/**
 * Home «سطح شما»: the level with its place in the ladder, every commission rate at a glance, the
 * condition that keeps this level and the distance to the next one (either metric is enough).
 */
export const LevelCard = ({ level }: { level: LevelCardView }) => {
    const rates = rateCard(level.rates);
    const next = level.next;
    const p = level.progress;
    const st = level.standing;
    const keeps = st.activeServices >= st.minActiveServices || st.newSales90d >= st.minNewSales90d;
    const hasCondition = st.minActiveServices > 0 || st.minNewSales90d > 0;

    return (
        <section className="overflow-hidden rounded-2xl bg-primary shadow-xs ring-1 ring-secondary ring-inset" aria-labelledby="level-title">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
                {/* Level + rates */}
                <div className="flex flex-col gap-5 p-5 md:p-6">
                    <div className="flex items-center gap-4">
                        <img src={levelImage(level.current.rank)} alt="" aria-hidden="true" draggable={false} className="size-16 shrink-0 select-none drop-shadow-sm md:size-20" />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <p className="flex flex-wrap items-center gap-1.5 text-sm text-tertiary">
                                {level.locked ? "همکاری قراردادی" : "سطح فعلی شما"}
                                {!level.locked && level.ladderSize > 0 && (
                                    <span className="text-quaternary">
                                        · سطح {formatNumber(level.current.rank)} از {formatNumber(level.ladderSize)}
                                    </span>
                                )}
                                <HelpTip term="level" />
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 id="level-title" className="text-display-xs font-semibold text-primary">
                                    {level.current.name}
                                </h2>
                                {level.locked && (
                                    <Badge type="pill-color" color="brand" size="sm">
                                        نرخ اختصاصی
                                    </Badge>
                                )}
                            </div>
                            {!level.locked && level.ladderSize > 1 && (
                                <div className="flex gap-1 pt-1" aria-hidden="true">
                                    {Array.from({ length: level.ladderSize }, (_, i) => (
                                        <span key={i} className={cx("h-1.5 w-6 rounded-full", i < level.current.rank ? "bg-brand-solid" : "bg-quaternary")} />
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button href="/levels" color="link-color" size="sm" iconTrailing={ArrowNext} className="shrink-0 max-sm:hidden">
                            همهٔ سطح‌ها
                        </Button>
                    </div>

                    <div>
                        <p className="mb-2.5 text-sm font-semibold text-secondary">پورسانت شما در این سطح</p>
                        <div className="grid grid-cols-2 gap-2.5 md:gap-3">
                            <RateTile icon={Link03} label="فروش جدید" value={pct(rates.linkBp)} note="با لینک، کد معرف یا کد تخفیف؛ فرقی ندارد" />
                            <RateTile icon={RefreshCcw01} label="تمدید" value={pct(rates.renewalBp)} note="هر بار که مشتری‌تان سرویس را تمدید کند" />
                        </div>
                    </div>
                </div>

                {/* Condition + next level */}
                <div className="flex flex-col gap-4 border-t border-secondary bg-secondary_alt p-5 md:p-6 lg:border-s lg:border-t-0">
                    {level.locked ? (
                        <div className="flex flex-col gap-2">
                            <p className="text-sm font-semibold text-secondary">شرایط همکاری شما</p>
                            <p className="text-sm leading-6 text-tertiary">{level.hint} اگر سؤالی دربارهٔ قرارداد دارید، از پشتیبانی بپرسید.</p>
                            <ContractSwitchAction
                                enabled={level.locked}
                                note="می‌توانید به‌جای درصد ثابت، مثل بقیهٔ همکاران با سطح‌ها جلو بروید و با فروش بیشتر درصد بالاتری بگیرید."
                                className="pt-1"
                            />
                        </div>
                    ) : (
                        <>
                            {hasCondition && (
                                <div className="flex flex-col gap-1.5">
                                    <p className="text-sm font-semibold text-secondary">شرط ماندن در سطح {level.current.name}</p>
                                    <p className="flex items-start gap-2 text-sm leading-6 text-tertiary">
                                        {keeps ? (
                                            <CheckCircle className="mt-1 size-4 shrink-0 text-fg-success-secondary" aria-hidden="true" />
                                        ) : (
                                            <AlertTriangle className="mt-1 size-4 shrink-0 text-fg-warning-secondary" aria-hidden="true" />
                                        )}
                                        <span>
                                            {formatNumber(st.minActiveServices)} سرویس فعال <span className="text-quaternary">یا</span> {formatNumber(st.minNewSales90d)} فروش در ۹۰ روز. الان{" "}
                                            <span className="font-medium text-secondary">{formatNumber(st.activeServices)} سرویس فعال</span> و{" "}
                                            <span className="font-medium text-secondary">{formatNumber(st.newSales90d)} فروش</span> دارید.
                                        </span>
                                    </p>
                                    <p className="text-xs text-quaternary">سطح هر سه ماه یک بار بازبینی می‌شود و یک‌باره پایین نمی‌آید.</p>
                                </div>
                            )}

                            {level.guaranteedUntil && (
                                <div className="flex gap-2 rounded-xl bg-success-primary p-3 text-sm text-success-primary ring-1 ring-green-200 ring-inset dark:ring-green-500/25">
                                    <Stars02 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                                    <span>همکار ویژه هستید: این سطح تا {formatJalali(level.guaranteedUntil, "long")} برایتان محفوظ است، حتی اگر به شرطش نرسید.</span>
                                </div>
                            )}

                            {level.graceUntil && (
                                <div className="flex gap-2 rounded-xl bg-warning-primary p-3 text-sm text-warning-primary ring-1 ring-yellow-200 ring-inset dark:ring-yellow-500/25">
                                    <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                                    <span>اگر تا {formatJalali(level.graceUntil, "long")} به شرط این سطح نرسید، سطح‌تان یک پله پایین می‌آید.</span>
                                </div>
                            )}

                            {next && p ? (
                                <div className={cx("flex flex-col gap-3", hasCondition && "border-t border-secondary pt-4")}>
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-sm font-semibold text-secondary">تا سطح {next.name}</p>
                                        <span className="text-xs text-tertiary digits-tabular">{formatPercentBp(p.progressBp)} راه</span>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center justify-between text-xs">
                                            <HelpTip term="activeService">
                                                <span className="text-tertiary">سرویس فعال</span>
                                            </HelpTip>
                                            <span className="text-tertiary digits-tabular">
                                                {formatNumber(p.activeServices.current)} از {formatNumber(p.activeServices.target)}
                                            </span>
                                        </div>
                                        <ProgressBar value={p.activeServices.progressBp} max={10000} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-tertiary">فروش ۹۰ روز اخیر</span>
                                            <span className="text-tertiary digits-tabular">
                                                {formatNumber(p.newSales90d.current)} از {formatNumber(p.newSales90d.target)}
                                            </span>
                                        </div>
                                        <ProgressBar value={p.newSales90d.progressBp} max={10000} progressClassName="bg-accent-500" />
                                    </div>
                                    <p className="text-sm leading-6 text-tertiary">{level.hint}</p>
                                </div>
                            ) : (
                                <p className={cx("text-sm leading-6 text-tertiary", hasCondition && "border-t border-secondary pt-4")}>{level.hint}</p>
                            )}
                        </>
                    )}

                    {(next && level.nextGift) || level.currentGift ? (
                        <div className="mt-auto flex gap-3 rounded-xl bg-accent-50 p-3.5 ring-1 ring-accent-200 ring-inset dark:bg-accent-500/10 dark:ring-accent-500/25">
                            <Gift01 className="mt-0.5 size-5 shrink-0 text-accent-700 dark:text-accent-400" aria-hidden="true" />
                            <div className="flex flex-col gap-0.5">
                                <p className="text-sm font-semibold text-accent-800 dark:text-accent-300">
                                    {next && level.nextGift ? `هدیهٔ سطح ${next.name}` : `هدیهٔ سطح ${level.current.name}`}
                                </p>
                                <p className="text-sm text-accent-800 dark:text-accent-200">{next && level.nextGift ? level.nextGift : level.currentGift}</p>
                            </div>
                        </div>
                    ) : null}

                    {!level.locked && !level.guaranteedUntil && (
                        <Link
                            to="/special"
                            className="flex items-center gap-2 rounded-xl p-3 text-sm ring-1 ring-secondary outline-focus-ring transition ring-inset hover:bg-primary_hover focus-visible:outline-2"
                        >
                            <Stars02 className="size-4 shrink-0 text-fg-brand-secondary" aria-hidden="true" />
                            <span className="text-secondary">
                                مخاطب بزرگی دارید؟ <span className="font-semibold text-brand-secondary">با همکاری ویژه از سطح بالاتر شروع کنید</span>
                            </span>
                        </Link>
                    )}

                    <Button href="/levels" color="secondary" size="sm" iconTrailing={ArrowNext} className="sm:hidden">
                        همهٔ سطح‌ها و شرایط
                    </Button>
                </div>
            </div>
        </section>
    );
};
